import { useId, type ComponentProps } from 'react';
import { Button, usePrefix } from '@carbon/react';
import type { Placement } from '@floating-ui/dom';
import classNames from 'classnames';
import { FloatingTooltip } from 'components/floating-tooltip';
import styles from './blocked-action.module.scss';

type ActionIcon = ComponentProps<typeof Button>['renderIcon'];

interface BlockedActionProps {
  label: string;
  reason: string;
  renderIcon: ActionIcon;
  kind?: 'ghost' | 'tertiary';
  iconOnly?: boolean;
  placement?: Placement;
}

/**
 * An action the user can't take yet. Carbon never opens a tooltip on a
 * disabled button, so this one stays focusable (aria-disabled), ignores
 * clicks, and is described by its reason, also shown as a tooltip on hover and
 * focus.
 *
 * @param {BlockedActionProps} props - The action's name, why it is blocked, and how it looks.
 * @return {JSX.Element} The blocked action.
 */
export function BlockedAction({
  label,
  reason,
  renderIcon,
  kind = 'ghost',
  iconOnly = false,
  placement = 'top',
}: BlockedActionProps) {
  const prefix = usePrefix();
  const descriptionId = useId();
  return (
    <>
      <FloatingTooltip
        content={reason}
        placement={placement}
        asChild
        describeTrigger={false}
      >
        <Button
          kind={kind}
          size="sm"
          renderIcon={renderIcon}
          // Carbon styles only :disabled and these classes; aria-disabled needs them.
          className={classNames(`${prefix}--btn--disabled`, styles.blocked, {
            [`${prefix}--btn--icon-only`]: iconOnly,
          })}
          aria-label={iconOnly ? label : undefined}
          aria-disabled="true"
          aria-describedby={descriptionId}
          onClick={(event: React.MouseEvent) => event.preventDefault()}
        >
          {iconOnly ? null : label}
        </Button>
      </FloatingTooltip>
      {/* Hidden, not visually hidden: aria-describedby still reads it, browse mode doesn't. */}
      <span id={descriptionId} hidden>
        {reason}
      </span>
    </>
  );
}

interface IconActionProps {
  label: string;
  renderIcon: ActionIcon;
  onClick: () => void;
  blockedReason?: string | null;
  disabled?: boolean;
  tooltipAlignment?: 'end';
}

/**
 * A row's icon action: Carbon's icon button with its label as the tooltip, or,
 * when blocked, a BlockedAction showing the reason instead, even while disabled.
 *
 * @param {IconActionProps} props - The action, and why it is blocked when it is.
 * @return {JSX.Element} The icon action.
 */
export function IconAction({
  label,
  renderIcon,
  onClick,
  blockedReason,
  disabled = false,
  tooltipAlignment,
}: IconActionProps) {
  if (blockedReason) {
    return (
      <BlockedAction
        label={label}
        reason={blockedReason}
        renderIcon={renderIcon}
        iconOnly
        placement={tooltipAlignment ? 'top-end' : 'top'}
      />
    );
  }
  return (
    <Button
      kind="ghost"
      size="sm"
      hasIconOnly
      renderIcon={renderIcon}
      iconDescription={label}
      tooltipPosition="top"
      tooltipAlignment={tooltipAlignment}
      disabled={disabled}
      onClick={onClick}
    />
  );
}
